import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy } from 'react'

import AppLayout from '@/components/layout/AppLayout'
import AuthLayout from '@/components/layout/AuthLayout'
import { PrivateRoute, PublicRoute } from '@/components/auth/PrivateRoute'

// Auth pages — eager (small, needed immediately)
import SignInPage from '@/pages/auth/SignInPage'
import SignUpPage from '@/pages/auth/SignUpPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'

// App pages — lazy (code splitting per route)
const SchedulePage      = lazy(() => import('@/pages/schedule/SchedulePage'))
const ZaloPage          = lazy(() => import('@/pages/zalo/ZaloPage'))
const DashboardPage     = lazy(() => import('@/pages/dashboard/DashboardPage'))
const CoursesPage       = lazy(() => import('@/pages/courses/CoursesPage'))
const CourseDetailPage  = lazy(() => import('@/pages/courses/CourseDetailPage'))
const UsersPage         = lazy(() => import('@/pages/users/UsersPage'))
const PathsPage         = lazy(() => import('@/pages/paths/PathsPage'))
const WebsitePage       = lazy(() => import('@/pages/website/WebsitePage'))
const ReportsPage       = lazy(() => import('@/pages/reports/ReportsPage'))
const CommunicationPage = lazy(() => import('@/pages/communication/CommunicationPage'))
const MarketingPage     = lazy(() => import('@/pages/marketing/MarketingPage'))
const SettingsPage      = lazy(() => import('@/pages/settings/SettingsPage'))
const SubscriptionsPage = lazy(() => import('@/pages/subscriptions/SubscriptionsPage'))
const NotFoundPage      = lazy(() => import('@/pages/NotFoundPage'))

function Loader() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

function S({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Loader />}>{children}</Suspense>
}

export default function App() {
  return (
    <Routes>
      {/* Public auth routes — redirect to /dashboard if already logged in */}
      <Route element={<PublicRoute />}>
        <Route element={<AuthLayout />}>
          <Route path="/sign-in"         element={<SignInPage />} />
          <Route path="/sign-up"         element={<SignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Route>
      </Route>

      {/* Protected app routes — redirect to /sign-in if not logged in */}
      <Route element={<PrivateRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/"              element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard"     element={<S><DashboardPage /></S>} />
          <Route path="/schedule"      element={<S><SchedulePage /></S>} />
          <Route path="/courses"       element={<S><CoursesPage /></S>} />
          <Route path="/courses/:id"   element={<S><CourseDetailPage /></S>} />
          <Route path="/users"         element={<S><UsersPage /></S>} />
          <Route path="/paths"         element={<S><PathsPage /></S>} />
          <Route path="/website"       element={<S><WebsitePage /></S>} />
          <Route path="/reports"       element={<S><ReportsPage /></S>} />
          <Route path="/communication" element={<S><CommunicationPage /></S>} />
          <Route path="/marketing"     element={<S><MarketingPage /></S>} />
          <Route path="/settings"      element={<S><SettingsPage /></S>} />
          <Route path="/subscriptions" element={<S><SubscriptionsPage /></S>} />
          <Route path="/zalo"          element={<S><ZaloPage /></S>} />
        </Route>
      </Route>

      {/* 404 */}
      <Route path="*" element={<S><NotFoundPage /></S>} />
    </Routes>
  )
}
